/**
 * MarkdownRenderer — Mintlify-inspired component engine for agent output.
 *
 * Ported verbatim from rmint_rwa_v6/src/components/canvas/markdown-renderer.tsx
 * (commit reference: see v6 git log). Imports adjusted for v7's Vite SPA
 * structure; the 'use client' directive is dropped because Vite has no
 * server-component boundary to declare. Otherwise this is the v6
 * implementation line-for-line, per INVESTOR_UX_2026-04-28.md §9 Q5.
 *
 * Display directives (Tier 1):
 *   :::callout[type]{title="..."}content:::     → Callout (info/warning/tip/danger)
 *   :::steps + :::step{title="..." status=""}   → Numbered step list
 *   :::card{title="" icon="" href=""}content:::  → Clickable card
 *   :::card-group{cols=2} + cards               → Card grid
 *   :::stat{label="" value="" change=""}:::      → Key metric
 *   :::stat-group + stats                        → Metric row
 *
 * Interactive widgets:
 *   :::widget[tx-sign]{...}:::                   → Transaction signing
 *   :::widget[kyc-upload]{...}:::                → KYC verification
 *   :::widget[form-invest]{...}:::               → Investment form
 *
 * Navigation:
 *   [Label](/prompt/text)                        → Clickable prompt button
 */

import { Streamdown } from 'streamdown';
import { mermaid } from '@streamdown/mermaid';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import { type ReactNode } from 'react';
import type { Plugin } from 'unified';
import type { Root } from 'mdast';
import { normalizeAgentMarkdown } from '@rmint/core/directives/normalize-agent-markdown';
import { useWidgetRegistry } from '../widgets/widget-registry';
import type { Widget } from '../widgets/types';

// ── Known directive names → custom HTML element names ─────────────────

const DIRECTIVE_MAP: Record<string, string> = {
  widget: 'widget-directive',
  callout: 'rmint-callout',
  steps: 'rmint-steps',
  step: 'rmint-step',
  card: 'rmint-card',
  'card-group': 'rmint-card-group',
  stat: 'rmint-stat',
  'stat-group': 'rmint-stat-group',
  faq: 'rmint-faq',
  q: 'rmint-faq-q',
};

// HTML attribute names are case-insensitive and the rehype pipeline
// normalizes them to lowercase. Widget components were authored to read
// camelCase props (e.g. `props.subscriptionId`). This map reverses the
// lowercase → camelCase for every known directive attribute used by any
// widget. Unknown attributes pass through unchanged. See registry tags
// in `allowedTags` (Streamdown sanitization) and per-widget Zod schemas
// in `packages/widgets/src/*/schema.ts`.
const ATTR_CAMELCASE_MAP: Record<string, string> = {
  cellname: 'cellName',
  walletaddress: 'walletAddress',
  investorid: 'investorId',
  subscriptionid: 'subscriptionId',
  restaurantid: 'restaurantId',
  tokenaddress: 'tokenAddress',
  saleaddress: 'saleAddress',
  contractaddress: 'contractAddress',
  amountusd: 'amountUsd',
  cellnodeid: 'cellNodeId',
  kycattestationid: 'kycAttestationId',
  walletapi: 'walletApi',
  investorname: 'investorName',
  restaurantname: 'restaurantName',
  txkind: 'txKind',
  returnprompt: 'returnPrompt',
  // Outreach-agent slice α.4 — research-finding widget directive props.
  findingid: 'findingId',
  // Outreach-agent slice α.5 — candidate-card + prospect-link directive props.
  // The 5 lowercase-keys-with-no-camel-equivalent (handle, channel, name,
  // reason_line, preview_score, label) round-trip via the registry's
  // identity branch in normalizeAttrCase and need no mapping entry. Only
  // names that DIFFER between lowercase HTML attr form and camelCase
  // component prop form need entries here.
  candidateid: 'candidateId',
  prospectid: 'prospectId',
  // Outreach-agent slice α.6 — draft-card directive props. body_preview,
  // subject, rationale, channel are single-word/snake_case and pass
  // through the identity branch in normalizeAttrCase unchanged; only
  // draftid needs a camelCase mapping entry.
  draftid: 'draftId',
  // Outreach-agent slice α.7 — founder-decision directive props.
  // tool_use_id is snake_case in the schema (matches the Anthropic
  // tool_use block's `tool_use_id`); the identity branch in
  // normalizeAttrCase already preserves it after the lowercase pass —
  // tool_use_id stays tool_use_id. question + options also identity.
  // sessionId is the only key that round-trips through the lowercase
  // pass as `sessionid` and needs the camelCase mapping back.
  sessionid: 'sessionId',
  // H.2.4 — device-pairing directive props. token + step are identity-safe
  // (lowercase passes through normalizeAttrCase unchanged); projectorurl is
  // the camelCase round-trip back to projectorUrl.
  projectorurl: 'projectorUrl',
  // H.3.4a — admin kyc-decision directive props. tool_use_id +
  // subscriptionid/investorid + sessionid are already mapped above; rmintid
  // and the three optional review fields are net-new for the admin widget.
  rmintid: 'rmintId',
  profilesnapshot: 'profileSnapshot',
  kycdocfilenames: 'kycDocFilenames',
  subagentfindingmarkdown: 'subAgentFindingMarkdown',
};

function normalizeAttrCase(props: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    out[ATTR_CAMELCASE_MAP[k] ?? k] = v;
  }
  return out;
}

// ── Remark plugin: transform ALL known directives ─────────────────────

function getDirectiveLabel(node: any): string {
  const firstChild = node.children?.[0];
  if (!firstChild) return '';
  // remark-directive marks the [bracket] label child with
  // data.directiveLabel = true. If present, extract its value AND
  // splice the child out of node.children — otherwise the literal
  // label text leaks into the rendered body alongside the actual
  // content (e.g. ":::callout[warning]{title=\"X\"}\nbody:::" was
  // rendering "warning" between the title and "body").
  if (firstChild.data?.directiveLabel) {
    let value = '';
    if (firstChild.type === 'paragraph') {
      value = firstChild.children?.[0]?.value ?? '';
    } else if (firstChild.type === 'text') {
      value = firstChild.value ?? '';
    }
    node.children = node.children.slice(1);
    return value;
  }
  // Backward-compat fallback (callers that don't set directiveLabel
  // on the first child — kept for safety though we don't know of any).
  if (firstChild.type === 'paragraph') {
    return firstChild.children?.[0]?.value ?? '';
  }
  if (firstChild.type === 'text') {
    return firstChild.value ?? '';
  }
  return '';
}

const remarkComponentDirective: Plugin<[], Root> = () => (tree) => {
  const transform = (node: any) => {
    const hName = DIRECTIVE_MAP[node.name];
    if (!hName) return;

    // Lowercase attribute keys before they reach hProperties. Streamdown
    // wraps remark→rehype with rehype-sanitize using our `allowedTags`
    // allowlist; that allowlist is lowercase per HTML's case-insensitive
    // attribute spec. hast-util-sanitize matches property names string-
    // exact, so an agent-emitted `subscriptionId="X"` (camelCase, preserved
    // by remark-directive) gets STRIPPED before normalizeAttrCase or any
    // widget-side fallback can recover it. Lowercasing here ensures the
    // sanitize stage sees a name that matches the allowlist; the renderer's
    // normalizeAttrCase below then maps lowercase → camelCase for the
    // React component. (D.2.11 normalizeAttrCase + D.2.12 widget dual-read
    // both target post-sanitize stages and are insufficient on their own.)
    const rawAttrs = node.attributes ?? {};
    const attributes = Object.fromEntries(
      Object.entries(rawAttrs).map(([k, v]) => [k.toLowerCase(), v]),
    );

    if (node.name === 'widget') {
      node.data = {
        hName,
        hProperties: { widgettype: getDirectiveLabel(node), ...attributes },
      };
    } else {
      node.data = {
        hName,
        hProperties: { label: getDirectiveLabel(node), ...attributes },
      };
    }
  };

  visit(tree, 'containerDirective', transform);
  visit(tree, 'leafDirective', transform);
  visit(tree, 'textDirective', transform);
};

// ── Remark plugin: list-shorthand inside card-group / stat-group ──────
//
// Cell templates encourage writing a card-group / stat-group like:
//
//     :::card-group{cols=2}
//     - {title="Browse offerings", icon="search", prompt="Show..."}
//     - {title="My holdings", icon="wallet", prompt="Show..."}
//     :::
//
// remark-directive parses the inner `- {...}` lines as a markdown list
// with literal-text items. There's no directive registered for "bare
// {...}" — so without this plugin, the shorthand surfaces as plain text
// in the UI. We walk card-group/stat-group container directives, find
// inner list children, and convert each list item's `{k="v", k="v"}`
// attribute bag into a synthetic leaf directive (`:card{...}` or
// `:stat{...}`) that the existing remarkComponentDirective above knows
// how to render.
//
// Card-specific: `prompt="X"` is rewritten to `href="/prompt/<encoded>"`
// because the Card React component keys its click handler off `href`.

function parseAttrBag(raw: string): Record<string, string> {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return {};
  const inner = trimmed.slice(1, -1);
  const attrs: Record<string, string> = {};
  const re = /(\w+)=(["'])((?:(?!\2).)*)\2/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(inner)) !== null) {
    attrs[m[1]!] = m[3]!;
  }
  return attrs;
}

function remapCardAttrs(attrs: Record<string, string>): Record<string, string> {
  if (!attrs.prompt || attrs.href) return attrs;
  const { prompt, ...rest } = attrs;
  return { ...rest, href: `/prompt/${encodeURIComponent(prompt)}` };
}

const remarkShorthandItems: Plugin<[], Root> = () => (tree) => {
  visit(tree, 'containerDirective', (node: any) => {
    if (node.name !== 'card-group' && node.name !== 'stat-group') return;
    const childDirectiveName = node.name === 'card-group' ? 'card' : 'stat';
    const remap =
      childDirectiveName === 'card' ? remapCardAttrs : (x: Record<string, string>) => x;

    const newChildren: any[] = [];
    for (const child of node.children ?? []) {
      if (child.type !== 'list') {
        newChildren.push(child);
        continue;
      }
      for (const li of child.children ?? []) {
        if (li.type !== 'listItem') continue;
        const para = li.children?.[0];
        const textNode = para?.children?.[0];
        if (!para || para.type !== 'paragraph' || !textNode || textNode.type !== 'text') {
          continue;
        }
        const raw = textNode.value ?? '';
        const attrs = parseAttrBag(raw);
        if (Object.keys(attrs).length === 0) continue;
        newChildren.push({
          type: 'leafDirective',
          name: childDirectiveName,
          attributes: remap(attrs),
          children: [],
        });
      }
    }
    node.children = newChildren;
  });
};

// ── Display Components ────────────────────────────────────────────────

const CALLOUT_STYLES: Record<string, { border: string; bg: string; icon: string; title: string }> = {
  info: { border: 'border-info-line', bg: 'bg-info-bg', icon: 'ℹ️', title: 'text-info' },
  warning: { border: 'border-warn-line', bg: 'bg-warn-bg', icon: '⚠️', title: 'text-warn' },
  tip: { border: 'border-success-line', bg: 'bg-success-bg', icon: '💡', title: 'text-success' },
  danger: { border: 'border-danger-line', bg: 'bg-danger-bg', icon: '🚨', title: 'text-danger' },
  success: { border: 'border-success-line', bg: 'bg-success-bg', icon: '✅', title: 'text-success' },
};

function Callout({ label, title, children }: { label?: string; title?: string; children?: ReactNode }) {
  const type = label || 'info';
  const style = CALLOUT_STYLES[type] || CALLOUT_STYLES.info!;
  return (
    <div className={`my-3 rounded-lg border ${style.border} ${style.bg} p-4`}>
      {title ? (
        <div className={`flex items-center gap-2 mb-2 text-sm font-semibold ${style.title}`}>
          <span>{style.icon}</span>
          <span>{title}</span>
        </div>
      ) : null}
      <div className="text-sm text-foreground/80 [&>div]:mb-1 [&>div:last-child]:mb-0">{children}</div>
    </div>
  );
}

function Steps({ children }: { children?: ReactNode }) {
  return (
    <div className="my-4 relative pl-8 space-y-0">
      <div className="absolute left-[14px] top-2 bottom-2 w-px bg-border" />
      {children}
    </div>
  );
}

function Step({ title, status, children }: { title?: string; status?: string; children?: ReactNode }) {
  const isComplete = status === 'complete' || status === 'done';
  const isActive = status === 'active' || status === 'current';
  return (
    <div className="relative pb-4">
      <div className={`absolute -left-8 top-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-bold ${
        isComplete
          ? 'border-success-line bg-success-bg text-success'
          : isActive
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border bg-background text-foreground-mute'
      }`}>
        {isComplete ? '✓' : isActive ? '●' : '○'}
      </div>
      {title ? (
        <span className={`block text-sm font-semibold mb-1 ${isComplete ? 'text-success' : isActive ? 'text-primary' : 'text-foreground'}`}>
          {title}
        </span>
      ) : null}
      <div className="text-sm text-foreground/80 [&>div]:mb-1 [&>div:last-child]:mb-0">{children}</div>
    </div>
  );
}

// Map named icon tokens to inline SVGs. Unknown names render as plain
// text so emoji passthrough still works.
const ICON_SVGS: Record<string, ReactNode> = {
  search: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  shield: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3Z" />
    </svg>
  ),
  chart: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M3 3v18h18" />
      <path d="m7 15 4-4 4 4 5-5" />
    </svg>
  ),
  wallet: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
      <path d="M20 12v4H6a2 2 0 0 0-2 2c0 1.1.9 2 2 2h12v-4" />
      <path d="M4 6v12" />
    </svg>
  ),
  doc: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  gift: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M20 12v10H4V12" />
      <path d="M2 7h20v5H2z" />
      <path d="M12 22V7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7Z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" />
    </svg>
  ),
  x: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
  sparkles: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M12 3v4m0 10v4M3 12h4m10 0h4" />
      <path d="m5.5 5.5 2.8 2.8m7.4 7.4 2.8 2.8M5.5 18.5l2.8-2.8m7.4-7.4 2.8-2.8" />
    </svg>
  ),
};

function renderIcon(icon?: string): ReactNode {
  if (!icon) return null;
  const key = icon.toLowerCase();
  if (key in ICON_SVGS) {
    return <span className="text-foreground-mute shrink-0 mt-0.5">{ICON_SVGS[key]}</span>;
  }
  return <span className="text-lg mt-0.5 shrink-0">{icon}</span>;
}

function Faq({ children }: { children?: ReactNode }) {
  return <div className="my-3 space-y-1">{children}</div>;
}

function FaqQ({ label, children }: { label?: string; children?: ReactNode }) {
  const question = label && label.trim().length > 0 ? label : '(no question)';
  return (
    <details className="rounded-card border border-border bg-card">
      <summary className="cursor-pointer p-3 text-sm font-semibold text-foreground">
        {question}
      </summary>
      <div className="px-3 pb-3 text-sm text-foreground/80">{children}</div>
    </details>
  );
}

function Card({
  title,
  icon,
  href,
  children,
  ...props
}: {
  title?: string;
  icon?: string;
  href?: string;
  children?: ReactNode;
  onPromptClick?: (text: string) => void;
}) {
  const isPromptLink = href?.startsWith('/prompt/');
  const handleClick = () => {
    if (isPromptLink && props.onPromptClick) {
      props.onPromptClick(decodeURIComponent(href!.slice('/prompt/'.length)));
    }
  };

  return (
    <button
      type="button"
      onClick={isPromptLink ? handleClick : undefined}
      className={`text-left w-full rounded-xl border border-border bg-card p-4 transition-all ${
        isPromptLink ? 'hover:border-primary/40 hover:shadow-sm cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {renderIcon(icon)}
        <div className="flex-1 min-w-0">
          {title ? <p className="text-sm font-semibold text-foreground mb-1">{title}</p> : null}
          <div className="text-xs text-foreground-mute [&>p]:mb-0">{children}</div>
        </div>
        {isPromptLink ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 text-foreground-mute shrink-0 mt-0.5">
            <path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clipRule="evenodd" />
          </svg>
        ) : null}
      </div>
    </button>
  );
}

function CardGroup({ cols, children }: { cols?: string; children?: ReactNode }) {
  const gridCols = cols === '3' ? 'grid-cols-3' : cols === '1' ? 'grid-cols-1' : 'grid-cols-2';
  return <div className={`my-3 grid gap-3 ${gridCols}`}>{children}</div>;
}

function stripMarkdown(s?: string): string {
  if (!s) return '';
  return s.replace(/\*\*/g, '').replace(/\*/g, '').replace(/__/g, '').replace(/_/g, '');
}

function Stat({ label, value, change }: { label?: string; value?: string; change?: string }) {
  const cleanValue = stripMarkdown(value);
  const cleanChange = stripMarkdown(change);
  const isPositive = cleanChange?.startsWith('+');
  const isNegative = cleanChange?.startsWith('-');
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <span className="block text-xs text-foreground-mute mb-1">{stripMarkdown(label)}</span>
      <span className="block text-xl font-bold text-foreground">{cleanValue}</span>
      {cleanChange ? (
        <span className={`block text-xs mt-1 ${isPositive ? 'text-success' : isNegative ? 'text-danger' : 'text-foreground-mute'}`}>
          {cleanChange}
        </span>
      ) : null}
    </div>
  );
}

function StatGroup({ children }: { children?: ReactNode }) {
  return <div className="my-3 grid grid-cols-2 sm:grid-cols-4 gap-3">{children}</div>;
}

interface MarkdownRendererProps {
  children: string;
  className?: string;
  onPromptClick?: (text: string) => void;
  /**
   * TreeNode id this markdown was rendered from. Plumbed into every
   * interactive widget so widget-state hydration can persist form
   * values through navigation. `null` for un-persisted content
   * (streaming buffer, error states). v6 wired this to /nodes/{id};
   * Phase 5 widgets ignore it (display-only); Phase 6 reactivates.
   */
  currentNodeId?: string | null;
  /**
   * Widgets recorded on the current TreeNode in document order. The
   * renderer maintains a per-render ordinal counter and feeds each
   * widget-directive the matching `widgets[n].id`. Defaults to `[]`.
   */
  widgets?: Widget[];
  /** Wallet address — forwarded to widgets that need auth'd writes. */
  walletAddress?: string;
  /**
   * Phase 9.1 — when true, Streamdown renders an animated caret at the
   * end of the last text node and applies fade-in animation to newly-
   * arriving content. Pass `message.status === 'streaming'` from chat
   * surfaces; pass `false` (or omit) for static cells / chip-rendered
   * content. Suppressed when content is fully rendered.
   */
  isAnimating?: boolean;
}

// ── Main component ────────────────────────────────────────────────────

export function MarkdownRenderer({
  children,
  className,
  onPromptClick,
  currentNodeId = null,
  widgets = [],
  walletAddress,
  isAnimating = false,
}: MarkdownRendererProps) {
  // Registry is injected via WidgetRegistryProvider (apps supply their
  // concrete widget map). No provider → empty registry → unknown-widget
  // fallthrough. The local symbol name is kept as `widgetRegistry` so the
  // per-widget lookup expression below is unchanged.
  const widgetRegistry = useWidgetRegistry();
  const normalized = normalizeAgentMarkdown(children);
  // Per-render ordinal counter so the Nth widget directive the React
  // tree mounts gets widgets[N]. Reset every render via a mutable box.
  const widgetCursor = { current: 0 };
  return (
    <div className={className}>
      <Streamdown
        // Phase 9.1 — migrated from react-markdown to streamdown for
        // streaming-aware rendering (incomplete-token handling, animated
        // caret, security via rehype-harden, syntax-highlighted code,
        // mermaid + math plugin readiness). Keeps the existing remark-
        // directive + custom-component pipeline unchanged.
        //
        // `allowedTags` is required because streamdown sanitizes by
        // default; our 8 custom HTML tags below would otherwise be
        // stripped at the rehype stage. The empty attribute list `[]`
        // means "this tag is permitted but with no per-tag attribute
        // allowlist" — directive props arrive via hProperties on the
        // hast node and reach our component overrides regardless.
        animated={isAnimating}
        isAnimating={isAnimating}
        // Phase 9.1 — scope streaming-only behavior to the actively-
        // streaming bubble. Static cells (chip-clicked, fully-resolved
        // markdown) render with parseIncompleteMarkdown=false because
        // streamdown's `remend` rewrite would otherwise mangle our
        // `:::stat-group ... :::` directive bodies (turning the inner
        // `- {label=... value=...}` shorthand items into raw text).
        // Streaming bubbles keep the unterminated-block handling for
        // mid-token resilience.
        mode={isAnimating ? 'streaming' : 'static'}
        parseIncompleteMarkdown={isAnimating}
        // Phase 9.1 — mermaid plugin enabled. Agent can emit ` ```mermaid `
        // fences (graph TD / sequenceDiagram / etc.) and they render as
        // SVG diagrams inline. Useful for compliance flow viz, lifecycle
        // diagrams, audit trace visualization.
        plugins={{ mermaid }}
        allowedTags={{
          'widget-directive': [
            'widgettype', 'cellname', 'walletaddress', 'investorid',
            'subscriptionid', 'restaurantid', 'tokenaddress', 'saleaddress',
            'amountusd', 'cellnodeid', 'kycattestationid', 'walletapi',
            'investorname',
            // Outreach-agent slice α.4 — research-finding props. Without
            // these in the allowlist rehype-sanitize strips them and the
            // card renders blank (the agent's text directive is the
            // canonical render path; see ChatDock + spec §4.4).
            'url', 'summary', 'source', 'findingid',
            // Outreach-agent slice α.5 — candidate-card + prospect-link.
            // Same sanitize-must-see-lowercase rule as α.4: keys here are
            // lowercase, ATTR_CAMELCASE_MAP maps back to camelCase before
            // the widget component receives them.
            'candidateid', 'handle', 'channel', 'name',
            'reason_line', 'preview_score',
            'prospectid', 'label',
            // Outreach-agent slice α.6 — draft-card props. prospectid +
            // channel already in the allowlist above (α.5 carryover);
            // only draftid + subject + body_preview + rationale are net-new.
            'draftid', 'subject', 'body_preview', 'rationale',
            // Outreach-agent slice α.7 — founder-decision directive props.
            // tool_use_id stays snake_case (matches the founder-decision
            // schema field name + the Anthropic tool_use block id); options
            // arrives as a double-encoded JSON string (parsed in the
            // widget). sessionid lowercases here + maps to sessionId via
            // ATTR_CAMELCASE_MAP above. question is identity-safe.
            'tool_use_id', 'question', 'options', 'sessionid',
            // Slice β — outreach tool-result widgets carry a base64-encoded JSON
            // payload in a single `payload` attribute (avoids the normalizer's
            // brace-truncation + quote collisions for structured/array data).
            'payload',
            // H.2.4 — device-pairing directive props. token + step + projectorurl
            // are scalar attrs (no base64 needed for a 3-field widget). Without
            // these in the allowlist rehype-sanitize strips them and the widget
            // renders the "No pairing token provided" fallback in production.
            // (projectorurl lowercases here; ATTR_CAMELCASE_MAP maps back to
            // projectorUrl for the component prop.)
            'token', 'projectorurl', 'step',
            // H.3.4a — admin kyc-decision directive props. tool_use_id +
            // subscriptionid + investorid + sessionid are already in the
            // allowlist above; rmintid + the three optional review fields are
            // net-new (without these rehype-sanitize strips them and the
            // KycDecision card renders without the IDs needed for resume).
            'rmintid', 'profilesnapshot', 'kycdocfilenames', 'subagentfindingmarkdown',
          ],
          'rmint-callout': ['type', 'title'],
          'rmint-steps': [],
          'rmint-step': ['title', 'status'],
          'rmint-card': ['title', 'icon', 'href'],
          'rmint-card-group': ['cols'],
          'rmint-stat': ['label', 'value', 'change'],
          'rmint-stat-group': [],
          'rmint-faq': [],
          'rmint-faq-q': ['label'],
        }}
        remarkPlugins={[
          remarkGfm,
          remarkDirective,
          // Shorthand list-item → leaf directive. MUST run before the
          // component-directive transformer so the synthetic leaves
          // get hProperties wired up like any other directive.
          remarkShorthandItems,
          remarkComponentDirective,
        ]}
        components={{
          // ── Interactive widgets ──────────────────────────────────
          // @ts-expect-error - custom element from remark plugin
          'widget-directive': ({
            widgettype,
            ...props
          }: { widgettype: string; [key: string]: string }) => {
            const WidgetComponent = widgetRegistry[widgettype];
            if (!WidgetComponent) {
              return (
                <div className="my-2 rounded border border-destructive/30 px-3 py-2 text-sm text-destructive">
                  Unknown widget: {widgettype}
                </div>
              );
            }
            const ordinal = widgetCursor.current++;
            const nodeWidget = widgets[ordinal];
            const widgetIdForOrdinal =
              nodeWidget && nodeWidget.kind === widgettype ? nodeWidget.id : undefined;
            // Phase 9.1 — HTML attribute names are normalized to lowercase
            // by the rehype pipeline (`subscriptionId` → `subscriptionid`).
            // Widget components were authored to read camelCase (e.g.,
            // `props.subscriptionId`); without this normalization they
            // receive `undefined` for every camelCase prop and fail with
            // "Missing X on widget" errors. Map back to camelCase for
            // every known directive attribute.
            const camelCaseProps = normalizeAttrCase(props);
            const baseProps: Record<string, unknown> = {
              ...camelCaseProps,
              widgetId: widgetIdForOrdinal,
              currentNodeId,
              walletAddress,
            };
            const widgetProps = (widgettype === 'tx-sign' || widgettype === 'form-invest' || widgettype === 'kyc-form')
              ? { ...baseProps, onComplete: onPromptClick }
              : baseProps;

            return <WidgetComponent {...widgetProps} />;
          },

          // ── Display components (custom elements from remark plugin) ──
          // Streamdown's Components type requires `ComponentType<Record<string,unknown> & ExtraProps>`;
          // our typed helpers (Callout, Stat, etc.) accept narrower prop shapes. Casting to `any` matches
          // the existing `@ts-expect-error` pattern used for widget-directive above (the runtime hProperties
          // come through as a loose dict regardless of the TS surface).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-callout': Callout as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-steps': Steps as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-step': Step as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-card': ((props: any) => <Card {...props} onPromptClick={onPromptClick} />) as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-card-group': CardGroup as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-stat': Stat as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-stat-group': StatGroup as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-faq': Faq as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'rmint-faq-q': FaqQ as any,

          // ── Standard markdown elements ───────────────────────────
          h1: ({ children: h }) => (
            <h1 className="text-2xl font-semibold tracking-tight mb-4 mt-2">{h}</h1>
          ),
          h2: ({ children: h }) => (
            <h2 className="text-lg font-semibold tracking-tight mb-3 mt-6">{h}</h2>
          ),
          h3: ({ children: h }) => (
            <h3 className="text-base font-semibold mb-2 mt-4">{h}</h3>
          ),
          // <div> instead of <p> to avoid hydration errors when custom
          // components (stat, card, widget) render block elements
          // inside what markdown sees as a "paragraph"
          p: ({ children: c }) => (
            <div className="text-sm leading-relaxed mb-3 text-foreground/90">{c}</div>
          ),
          blockquote: ({ children: c }) => (
            <blockquote className="border-l-4 border-primary/40 pl-4 my-3 text-sm text-foreground-mute italic">
              {c}
            </blockquote>
          ),
          hr: () => <hr className="my-6 border-border" />,
          ul: ({ children: c }) => (
            <ul className="list-disc list-inside text-sm space-y-1 mb-3 ml-4">{c}</ul>
          ),
          ol: ({ children: c }) => (
            <ol className="list-decimal list-inside text-sm space-y-1 mb-3 ml-4">{c}</ol>
          ),
          li: ({ children: c }) => <li className="text-foreground/90">{c}</li>,
          code: ({ children: c, className: cls }) => {
            const isBlock = cls?.startsWith('language-');
            if (isBlock) {
              return (
                <pre className="bg-muted rounded-md p-4 overflow-x-auto my-3">
                  <code className="text-xs font-mono">{c}</code>
                </pre>
              );
            }
            return <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{c}</code>;
          },
          table: ({ children: c }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full text-sm border-collapse">{c}</table>
            </div>
          ),
          th: ({ children: c }) => (
            <th className="text-left font-medium px-3 py-2 border-b border-border text-foreground-mute text-xs uppercase tracking-wider">
              {c}
            </th>
          ),
          td: ({ children: c }) => (
            <td className="px-3 py-2 border-b border-border/50 text-foreground/80">{c}</td>
          ),
          a: ({ href, children: linkChildren }) => {
            if (href?.startsWith('/prompt/') && onPromptClick) {
              const promptText = decodeURIComponent(href.slice('/prompt/'.length));
              return (
                <button
                  type="button"
                  onClick={() => onPromptClick(promptText)}
                  className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:border-primary/50 cursor-pointer"
                >
                  {linkChildren}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 opacity-60">
                    <path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clipRule="evenodd" />
                  </svg>
                </button>
              );
            }
            return (
              <a
                href={href}
                className="text-primary underline underline-offset-2 hover:text-primary/80"
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                {linkChildren}
              </a>
            );
          },
        }}
      >
        {normalized}
      </Streamdown>
    </div>
  );
}
